export const helper = {
  stringOperatorsField(
    include = null,
    includeUnary = false,
    name = 'operator'
  ) {
    return {
      type: 'dropdown',
      name,
      values: [
        {
          name: 'equals',
          caption: 'equals'
        },
        {
          name: 'does not equal',
          caption: "doesn't equal"
        },
        ...(includeUnary
          ? [
              {
                name: 'is set',
                caption: 'is set'
              },
              {
                name: 'is not set',
                caption: 'is blank (not set)'
              }
            ]
          : []),
        {
          name: 'contains',
          caption: 'contains'
        },
        {
          name: 'does not contain',
          caption: "doesn't contain"
        },
        {
          name: 'starts with',
          caption: 'starts with'
        },
        {
          name: 'ends with',
          caption: 'ends with'
        },
        {
          name: 'gt',
          caption: 'is greater than'
        },
        {
          name: 'gte',
          caption: 'is greater than or equal to'
        },
        {
          name: 'lt',
          caption: 'is less than'
        },
        {
          name: 'lte',
          caption: 'is less than or equal to'
        },
        {
          name: 'glob',
          caption: 'wildcard match'
        }
      ].filter(include ? (v) => include.includes(v.name) : (v) => true)
    }
  },

  pathPlaceholder(fieldList, fieldModel) {
    switch (fieldModel.operator) {
      case 'equals':
      case 'does not equal':
        return '/blog/some-article'

      case 'contains':
      case 'does not contain':
      case 'starts with':
      case 'ends with':
        return '/blog'

      case 'glob':
        return '*/blog/*'
    }

    return ''
  },

  urlPlaceholder(fieldList, fieldModel) {
    switch (fieldModel.operator) {
      case 'equals':
      case 'does not equal':
        return 'https://example.com/blog/some-article'

      case 'contains':
      case 'does not contain':
      case 'starts with':
      case 'ends with':
        return '/blog/'

      case 'glob':
        return '*.example.com/blog/*'
    }

    return ''
  }
}
export const getQueryParamOccurrenceValues = () => [
  {
    name: 'last touch',
    caption: 'on most recent occurrence'
  },
  {
    name: 'last',
    caption: 'on current page'
  },
  {
    name: 'first touch',
    caption: 'on first occurrence'
  },
  {
    name: 'first',
    caption: 'on first ever page hit'
  },
  {
    name: 'any',
    caption: 'on any occurrence'
  }
]

//
// makeQueryParamFields creates and returns an array of fields common for query params:
// - occurrence (dropdown)
// - operator (dropdown)
// - value (text)
//
const makeQueryParamFields = () => [
  {
    type: 'dropdown',
    name: 'occurrence',
    values: getQueryParamOccurrenceValues()
  },
  helper.stringOperatorsField(null, true),
  {
    type: 'text',
    name: 'value',

    condition(fieldModelSoFar) {
      return !['is set', 'is not set'].includes(
        (fieldModelSoFar || {}).operator
      )
    }
  }
]

//
// makeQueryParamType creates and returns a type description for a named query param (e.g. "utm_source").
//
const makeQueryParamType = (param) => ({
  name: param,
  caption: param,
  fields: makeQueryParamFields()
})

//
// makePartnerSource creates and returns a source description for a particular integration partner (e.g. Drip).
// Intended to DRY up partner declarations, which are 99.44% identical.
//
// Required parameters:
// - partnerName (PartnerName)
// - caption (string): human-readable name for the partner
//
// Options supported:
// - tagSupport (boolean|Object): controls support for a 'tags' field in the output; defaults to `false`
// - customFieldSupport (boolean|Object): ditto for 'customField'; default `false`
// - listSupport (boolean|Object): ditto for 'customField'; default `false`
// - defaultIconClass (string): value returned from `iconClass` when one isn't indicated by `item.$type`
//
// Regarding tagSupport and customFieldSupport:
// - `false`: no fields will be generated, because we're saying the partner doesn't have the concept of (e.g.) tags.
// - <something truthy>: fields will be generated, but we don't have a fancy "create new ones" feature unless you pass a
// config object like...
// - { create: <something truthy> }
//
const makePartnerSource = (partnerName, caption, options = {}) => {
  // Assign default option values.
  options = Object.assign(
    {
      tagSupport: false,
      listSupport: false,
      customFieldSupport: false,
      defaultIconClass: 'fa fa-envelope'
    },
    options
  )

  if (!caption) {
    throw 'missing or invalid caption'
  }

  if (!(options.customFieldSupport || !options.tagSupport)) {
    throw `partner '${partnerName}' has neither custom field nor tag support enabled`
  }

  return {
    name: partnerName,
    caption,

    summary(item) {
      let x = caption

      if (item['$type'] === 'tags') {
        x += ` tags ${item['operator']} ${item['tagIds']}`
      } else if (item['$type'] === 'customField') {
        x += ` ${item['customFieldId']} field: ${item['value']}`
      } else if (item['$type'] === 'anonymous') {
        x += item['operator'] === 'is_false' ? ' - subscriber' : ' - anonymous'
      }

      return x || caption
    },

    iconClass(item) {
      switch (item['$type']) {
        case 'tags':
          return 'fa fa-tag'

        case 'customField':
          return 'fa fa-id-card-o'

        case 'anonymous':
          return 'fa fa-id-badge'

        default:
          return options.defaultIconClass
      }
    },

    types: [
      {
        name: 'isAnonymous',
        caption: 'Anonymous',
        fields: []
      },
      {
        name: 'isSubscriber',
        caption: 'Subscriber',
        fields: []
      }, //
      // Tag support
      //
      ...(!options.tagSupport
        ? []
        : [
            {
              name: 'tags',
              caption: options.tagSupport.label || 'Tags',
              fields: [
                {
                  type: 'dropdown',
                  name: 'operator',
                  values: [
                    {
                      name: 'include any',
                      caption: 'include any of'
                    },
                    {
                      name: 'do not include any',
                      caption: "don't include any of"
                    }
                  ]
                },
                {
                  // TODO DRY selectizeOptions/values with Offer.vue (perhaps by centralizing in partnerData?)
                  type: 'autocomplete',
                  options: {
                    multiple: true,
                    selectizeOptions: {
                      //
                      // Support creation of novel tags
                      //
                      ...(!options.tagSupport.create
                        ? {}
                        : {
                            createFilter(input) {
                              const pattern = /^[A-Za-z0-9_\- ]+$/
                              return pattern.test(input)
                            },

                            create(input, callback) {
                              return {
                                id: input,
                                caption: input
                              }
                            }
                          })
                    }
                  },
                  name: 'tagIds',
                  placeholder: 'Pick a tag',

                  values(item) {
                    const extras = options.tagSupport.create
                      ? item.tagIds || []
                      : []
                    console.log('pp', partnerData)
                    return partnerData.getValuesForAutocompleter(
                      partnerName,
                      PartnerDataTypes.TAGS,
                      extras,
                      'read'
                    )
                  }
                }
              ]
            }
          ]), //
      // Custom field support
      //
      ...(!options.customFieldSupport
        ? []
        : [
            {
              name: 'customField',
              caption: options.customFieldSupport.label || 'Custom Field',
              fields: [
                {
                  type: options.customFieldSupport.unstructured
                    ? 'text'
                    : 'autocomplete',
                  options: {
                    selectizeOptions: {
                      //
                      // Support creation of novel custom fields
                      //
                      ...(!options.customFieldSupport.create
                        ? {}
                        : {
                            createFilter(input) {
                              const pattern = /^[A-Za-z0-9_]+$/
                              //console.log('s1 filter', input, pattern.test(input));
                              return pattern.test(input)
                            },

                            create(input, callback) {
                              return {
                                id: input,
                                caption: input
                              }
                            }
                          })
                    }
                  },
                  name: 'customFieldId',
                  placeholder:
                    options.customFieldSupport.placeholder ||
                    'Pick a custom field',

                  values(item) {
                    const integrationStore = useIntegrationStore()
                    integrationStore.enableFetch('custom_fields')
                    const customFields = computed(
                      () => integrationStore.partnerData.custom_fields
                    )
                    return customFields
                  }
                },
                helper.stringOperatorsField(null, true),
                {
                  type: 'text',
                  name: 'value',

                  condition(fieldModelSoFar) {
                    return !['is set', 'is not set'].includes(
                      (fieldModelSoFar || {}).operator
                    )
                  }
                }
              ]
            }
          ]), //
      // List support
      //
      ...(!options.listSupport
        ? []
        : [
            {
              name: 'lists',
              caption: options.listSupport.label || 'Lists',
              fields: [
                {
                  type: 'dropdown',
                  name: 'operator',
                  values: [
                    {
                      name: 'include any',
                      caption: 'visitor belongs to any of'
                    },
                    {
                      name: 'do not include any',
                      caption: "visitor doesn't belong to any of"
                    }
                  ]
                },
                {
                  type: 'autocomplete',
                  options: {
                    multiple: true,
                    selectizeOptions: {
                      //
                      // Support creation of novel lists
                      //
                      ...(!options.listSupport.create
                        ? {}
                        : {
                            createFilter(input) {
                              const pattern = /^[A-Za-z0-9_\- ]+$/
                              return pattern.test(input)
                            },

                            create(input, callback) {
                              return {
                                id: input,
                                caption: input
                              }
                            }
                          })
                    }
                  },
                  name: 'listIds',
                  placeholder: options.listSupport.placeholder || 'Pick a list',

                  values(item) {
                    const extras = options.listSupport.create
                      ? item.listIds || []
                      : []
                    return partnerData.getValuesForAutocompleter(
                      partnerName,
                      PartnerDataTypes.LISTS,
                      extras,
                      'read'
                    )
                  }
                }
              ]
            }
          ])
    ]
  }
}

const factory = (factoryOptions = {}) => {
  const { cms, dimensions = [], partners = [] } = factoryOptions

  const availableFeatures =
    (window.user && window.user.bits.availableFeatures) || {}
  const isAllowedEspPlaceholderSource =
    availableFeatures.rightbar === 'visible' ||
    availableFeatures.integrations_outside_rightbar === 'visible'
  const sources = [
    ...partners.map((p) => {
      return makePartnerSource(p.name, p.label, {
        customFieldSupport: p.espFeatureSupport.customField || false,
        tagSupport: p.espFeatureSupport.tag || false,
        listSupport: p.espFeatureSupport.list || false
      })
    }),
    ...(partners.length || !isAllowedEspPlaceholderSource
      ? []
      : [
          {
            name: 'espplaceholder',
            caption: 'Email list',
            types: [
              {
                name: 'isAnonymous',
                caption: 'Anonymous',
                fields: []
              },
              {
                name: 'isSubscriber',
                caption: 'Subscriber',
                fields: []
              }
            ]
          }
        ]),
    {
      summary(item) {
        let x = ''

        if (item['$type'] === 'custom') {
          x += 'Referred from ' + item['custom_referrer']
        }

        return x || 'Referrer'
      },

      iconClass: 'fa fa-arrow-right',
      name: 'referrer',
      caption: 'Referred By',
      types: [
        {
          name: 'direct',
          caption: 'no one (direct traffic)',
          fields: []
        },
        {
          name: 'referral',
          caption: 'any site (not direct traffic)',
          fields: []
        }, // {
        //     name: "search",
        //     caption: "a search engine"
        // },
        // {
        //     name: "social",
        //     caption: "a social network"
        // },
        {
          name: 'domain',
          caption: 'Specific domain',
          fields: [
            {
              type: 'text',
              name: 'domain',
              placeholder: 'example.com'
            }
          ]
        },
        {
          name: 'custom',
          caption: 'Specific URL',
          fields: [
            helper.stringOperatorsField(
              [
                'equals',
                'does not equal',
                'is set',
                'is not set',
                'contains',
                'does not contain',
                'glob'
              ],
              true
            ),
            {
              type: 'text',
              name: 'custom_referrer',
              placeholder: helper.urlPlaceholder,

              condition(fieldModelSoFar) {
                return !['is set', 'is not set'].includes(
                  (fieldModelSoFar || {}).operator
                )
              }
            }
          ]
        }
      ]
    },
    ...((dimensions || []).length === 0
      ? []
      : [
          {
            name: 'segments',
            caption: 'Segments',

            summary(item) {
              const name =
                window.user.bits.savedsegmentsMap[item['segmentId']] || '???'
              return (
                (item.negate === 'yes' ? 'Is not' : 'Is') +
                ' in the ' +
                name +
                ' segment'
              )
            },

            iconClass(item) {
              return item.negate === 'yes'
                ? 'fa fa-user-circle'
                : 'fa fa-user-circle-o'
            },

            types: dimensions.map((dimension) => ({
              name: dimension.id,
              caption: dimension.name,
              fields: [
                {
                  type: 'dropdown',
                  name: 'operator',
                  values: [
                    {
                      name: 'include any',
                      caption: 'include any of'
                    },
                    {
                      name: 'do not include any',
                      caption: "don't include any of"
                    }
                  ]
                },
                {
                  type: 'autocomplete',
                  options: {
                    multiple: true
                  },
                  name: 'segmentId',
                  values: (dimension.segments || []).map((segment) => ({
                    id: segment.id,
                    caption: segment.name
                  }))
                }
              ]
            }))
          }
        ]),
    {
      summary(item) {
        let x = ''
        x += 'Viewed '

        if (item['$type'] === 'path') {
          x += item['value'] + ' '
        }

        if (item['occurrence'] === 'any 2') {
          x += 'at least twice '
        } else if (item['occurrence'] === 'any 3') {
          x += 'at least three times '
        }

        return x.trim() || 'Viewed a page'
      },

      iconClass: 'fa fa-eye',
      name: 'pageviews',
      caption: 'On-Site Behavior',
      types: [
        {
          name: 'landingpage',
          caption: 'Original landing page',
          fields: [
            helper.stringOperatorsField(),
            {
              type: 'text',
              name: 'value',
              placeholder: helper.pathPlaceholder
            }
          ]
        },
        {
          name: 'path',
          caption: 'Pages viewed',
          fields: [
            helper.stringOperatorsField(),
            {
              type: 'text',
              name: 'value',
              placeholder: helper.pathPlaceholder
            },
            {
              type: 'dropdown',
              name: 'occurrence',
              values: [
                {
                  name: 'last',
                  caption: 'for the current page',
                  fields: []
                },
                {
                  name: 'penultimate',
                  caption: 'for the previous page',
                  fields: []
                },
                {
                  name: 'any',
                  caption: 'visited at least once',
                  fields: [
                    {
                      type: 'dropdown',
                      name: 'timeframe',
                      values: [
                        {
                          name: 'ever',
                          caption: 'ever'
                        },
                        {
                          name: 'this 7 days',
                          caption: 'in the last 7 days'
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'any 2',
                  caption: 'visited at least twice',
                  fields: [
                    {
                      type: 'dropdown',
                      name: 'timeframe',
                      values: [
                        {
                          name: 'ever',
                          caption: 'ever'
                        },
                        {
                          name: 'this 7 days',
                          caption: 'in the last 7 days'
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'any 3',
                  caption: 'visited at least three times',
                  fields: [
                    {
                      type: 'dropdown',
                      name: 'timeframe',
                      values: [
                        {
                          name: 'ever',
                          caption: 'ever'
                        },
                        {
                          name: 'this 7 days',
                          caption: 'in the last 7 days'
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          name: 'newvisitor',
          caption: 'First-time visitor',
          fields: []
        },
        {
          name: 'returningvisitor',
          caption: 'Returning visitor',
          fields: [
            {
              type: 'dropdown',
              name: 'timeframe',
              values: [
                {
                  name: 'ever',
                  caption: 'has ever visited the website before'
                },
                {
                  name: 'within',
                  caption: 'has visited in the last...'
                },
                {
                  name: 'outside',
                  caption: "hasn't visited in the last..."
                }
              ]
            },
            {
              type: 'text',
              name: 'duration',
              placeholder: '1',
              small: true,

              condition(fieldModelSoFar) {
                return ['within', 'outside'].includes(fieldModelSoFar.timeframe)
              }
            },
            {
              type: 'dropdown',
              name: 'unit',
              values: [
                {
                  name: 'week',
                  caption: 'weeks'
                },
                {
                  name: 'day',
                  caption: 'days'
                },
                {
                  name: 'hour',
                  caption: 'hours'
                }
              ],

              condition(fieldModelSoFar) {
                return ['within', 'outside'].includes(fieldModelSoFar.timeframe)
              }
            }
          ]
        }
      ]
    }, // {
    //   iconClass: "fa fa-desktop",
    //   name: "device",
    //   caption: "Device",
    //   types: [
    //     {
    //       name: "os",
    //       caption: "Operating System",
    //       fields: [
    //         {
    //             type: "dropdown",
    //             name: "operator",
    //             values: [
    //                 {
    //                     name: "include any",
    //                     caption: "include any of"
    //                 },
    //                 {
    //                     name: "do not include any",
    //                     caption: "don't include any of"
    //                 },
    //             ]
    //         },
    //         {
    //           type: "autocomplete",
    //           options: {
    //               multiple: true
    //           },
    //           name: 'operatingSystems',
    //           values: ['iOS', 'Android', 'MacOS', 'Windows', 'Linux'].map(v => ({
    //                   id: v, caption: v,
    //               }))
    //         }
    //       ]
    //     },
    //     {
    //       name: "browser",
    //       caption: "Browser",
    //       fields: [
    //         {
    //             type: "dropdown",
    //             name: "operator",
    //             values: [
    //                 {
    //                     name: "include any",
    //                     caption: "include any of"
    //                 },
    //                 {
    //                     name: "do not include any",
    //                     caption: "don't include any of"
    //                 },
    //             ]
    //         },
    //         {
    //           type: "autocomplete",
    //           options: {
    //               multiple: true
    //           },
    //           name: 'browsers',
    //           values: ['Chrome', 'Firefox', 'Safari', 'Internet Explorer', 'Edge', 'Opera'].map(v => ({
    //                   id: v, caption: v,
    //               }))
    //         }
    //       ]
    //     }
    //   ]
    // },
    {
      summary(item) {
        let x = ''
        x += item['query'].replace('utm_', 'UTM ') + ': ' + item['value']
        return x || 'UTM parameters'
      },

      iconClass: 'fa fa-sign-in',
      name: 'utm',
      caption: 'UTM Parameters',
      types: [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content'
      ].map(makeQueryParamType)
    },
    {
      summary(item) {
        let x = ''
        x += item['query'] + ' query parameter - ' + item['value']
        return x || 'Query parameters'
      },

      iconClass: 'fa fa-question',
      name: 'query',
      caption: 'Query Parameters',
      types: [
        {
          name: 'custom',
          caption: 'Custom',
          fields: [
            {
              type: 'text',
              name: 'query',
              placeholder: 'parameter name'
            },
            ...makeQueryParamFields()
          ]
        }
      ]
    },
    {
      name: 'jsdata',
      caption: 'Custom Data',
      types: [
        {
          name: 'windowVariable',
          caption: 'Javascript value',
          fields: [
            {
              type: 'text',
              name: 'variable',
              placeholder: 'mydata.industry'
            },
            helper.stringOperatorsField(null, true),
            {
              type: 'text',
              name: 'value',
              placeholder: 'healthcare',

              condition(fieldModelSoFar) {
                return !['is set', 'is not set'].includes(
                  (fieldModelSoFar || {}).operator
                )
              }
            }
          ]
        }
      ]
    },
    ...(cms === 'wordpress'
      ? [
          {
            name: 'wordpress',
            caption: 'WordPress',
            types: [
              {
                name: 'current-pageId',
                caption: 'Current page',
                fields: [
                  {
                    type: 'dropdown',
                    name: 'operator',
                    values: [
                      {
                        name: 'include any',
                        caption: 'is any of'
                      },
                      {
                        name: 'do not include any',
                        caption: "isn't any of"
                      }
                    ]
                  },
                  {
                    type: 'autocomplete',
                    name: 'value',
                    options: {
                      multiple: true
                    },
                    placeholder: 'Pick a page',

                    values() {
                      return cmsData.getValuesForAutocompleter(
                        'wordpress',
                        'pages'
                      )
                    }
                  }
                ]
              },
              {
                name: 'current-postId',
                caption: 'Current post',
                fields: [
                  {
                    type: 'dropdown',
                    name: 'operator',
                    values: [
                      {
                        name: 'include any',
                        caption: 'is any of'
                      },
                      {
                        name: 'do not include any',
                        caption: "isn't any of"
                      }
                    ]
                  },
                  {
                    type: 'autocomplete',
                    name: 'value',
                    options: {
                      multiple: true
                    },
                    placeholder: 'Pick a post',

                    values() {
                      return cmsData.getValuesForAutocompleter(
                        'wordpress',
                        'posts'
                      )
                    }
                  }
                ]
              },
              {
                name: 'current-taxonomy-post_tag',
                caption: "Current post's tags",
                fields: [
                  {
                    type: 'dropdown',
                    name: 'operator',
                    values: [
                      {
                        name: 'include any',
                        caption: 'include any of'
                      },
                      {
                        name: 'do not include any',
                        caption: "don't include any of"
                      }
                    ]
                  },
                  {
                    type: 'autocomplete',
                    name: 'value',
                    options: {
                      multiple: true
                    },
                    placeholder: 'Pick a tag',

                    values(item) {
                      return cmsData
                        .getValues('wordpress', 'taxonomies')
                        .then((taxonomies) =>
                          taxonomies.find((t) => t.id == 'post_tag')
                        )
                        .then((taxonomy) => (taxonomy ? taxonomy.terms : []))
                    }
                  }
                ]
              },
              {
                name: 'current-taxonomy-category',
                caption: "Current post's category",
                fields: [
                  {
                    type: 'dropdown',
                    name: 'operator',
                    values: [
                      {
                        name: 'include any',
                        caption: 'is any of'
                      },
                      {
                        name: 'do not include any',
                        caption: "isn't any of"
                      }
                    ]
                  },
                  {
                    type: 'autocomplete',
                    name: 'value',
                    options: {
                      multiple: true
                    },
                    placeholder: 'Pick a category',

                    values(item) {
                      return cmsData
                        .getValues('wordpress', 'taxonomies')
                        .then((taxonomies) =>
                          taxonomies.find((t) => t.id == 'category')
                        )
                        .then((taxonomy) => (taxonomy ? taxonomy.terms : []))
                    }
                  }
                ]
              },
              {
                name: 'favorite-taxonomy-post_tag',
                caption: 'Favorite tag',
                fields: [
                  {
                    type: 'dropdown',
                    name: 'operator',
                    values: [
                      {
                        name: 'include any',
                        caption: 'is any of'
                      },
                      {
                        name: 'do not include any',
                        caption: "isn't any of"
                      }
                    ]
                  },
                  {
                    type: 'autocomplete',
                    name: 'value',
                    options: {
                      multiple: true
                    },
                    placeholder: 'Pick a tag',

                    values(item) {
                      return cmsData
                        .getValues('wordpress', 'taxonomies')
                        .then((taxonomies) =>
                          taxonomies.find((t) => t.id == 'post_tag')
                        )
                        .then((taxonomy) => (taxonomy ? taxonomy.terms : []))
                    }
                  }
                ]
              },
              {
                name: 'favorite-taxonomy-category',
                caption: 'Favorite category',
                fields: [
                  {
                    type: 'dropdown',
                    name: 'operator',
                    values: [
                      {
                        name: 'include any',
                        caption: 'is any of'
                      },
                      {
                        name: 'do not include any',
                        caption: "isn't any of"
                      }
                    ]
                  },
                  {
                    type: 'autocomplete',
                    name: 'value',
                    options: {
                      multiple: true
                    },
                    placeholder: 'Pick a category',

                    values(item) {
                      return cmsData
                        .getValues('wordpress', 'taxonomies')
                        .then((taxonomies) =>
                          taxonomies.find((t) => t.id == 'category')
                        )
                        .then((taxonomy) => (taxonomy ? taxonomy.terms : []))
                    }
                  }
                ]
              } // {
              //     name: "terms",
              //     caption: "Custom taxonomy terms",
              //     fields: [
              //         {
              //             type: "autocomplete",
              //             name: "taxonomyId",
              //             placeholder: 'Pick a taxonomy',
              //             values() {
              //                 return cmsData.getValuesForAutocompleter('wordpress', 'taxonomies');
              //             },
              //         },
              //         {
              //             type: "dropdown",
              //             name: "operator",
              //             values: [
              //                 {
              //                     name: "include any",
              //                     caption: "include any of"
              //                 },
              //                 {
              //                     name: "do not include any",
              //                     caption: "don't include any of"
              //                 },
              //             ]
              //         },
              //         {
              //             type: "autocomplete",
              //             name: "value",
              //             options: {
              //                 multiple: true,
              //             },
              //             placeholder: "Pick a term",
              //             values(item) {
              //                 return cmsData.getValues('wordpress', 'taxonomies')
              //                     .then(taxonomies => taxonomies.find(t => t.id == item.taxonomyId))
              //                     .then(taxonomy => taxonomy ? taxonomy.terms : []);
              //             },
              //         },
              //     ]
              // },
              // {
              //     name: "favoriteTerms-terms",
              //     caption: "Favorite custom taxonomy terms",
              //     fields: [
              //         {
              //             type: "autocomplete",
              //             name: "taxonomyId",
              //             placeholder: 'Pick a taxonomy',
              //             values() {
              //                 return cmsData.getValuesForAutocompleter('wordpress', 'taxonomies');
              //             },
              //         },
              //         {
              //             type: "dropdown",
              //             name: "operator",
              //             values: [
              //                 {
              //                     name: "include any",
              //                     caption: "include any of"
              //                 },
              //                 {
              //                     name: "do not include any",
              //                     caption: "don't include any of"
              //                 },
              //             ]
              //         },
              //         {
              //             type: "autocomplete",
              //             name: "value",
              //             options: {
              //                 multiple: true,
              //             },
              //             placeholder: "Pick a term",
              //             values(item) {
              //                 return cmsData.getValues('wordpress', 'taxonomies')
              //                     .then(taxonomies => taxonomies.find(t => t.id == item.taxonomyId))
              //                     .then(taxonomy => taxonomy ? taxonomy.terms : []);
              //             },
              //         },
              //     ]
              // },
            ]
          }
        ]
      : []), // {
    //     name: "device",
    //     caption: "Device size / type",
    //     fields: [
    //         {
    //             type: "dropdown",
    //             name: "type",
    //             values: [
    //                 {
    //                     name: "size - mobile",
    //                     caption: "Screen size - mobile"
    //                 },
    //                 {
    //                     name: "size - tablet",
    //                     caption: "Screen size - tablet"
    //                 },
    //                 {
    //                     name: "size - desktop",
    //                     caption: "Screen size - desktop"
    //                 },
    //                 {
    //                     name: "ios",
    //                     caption: "iOS"
    //                 },
    //                 {
    //                     name: "iphone",
    //                     caption: "iPhone"
    //                 },
    //                 {
    //                     name: "ipad",
    //                     caption: "iPad"
    //                 },
    //                 {
    //                     name: "android",
    //                     caption: "Android"
    //                 }
    //             ]
    //         }
    //     ]
    // },
    {
      name: 'smartsegments',
      caption: 'Belongs to Smart Segment (coming soon)',
      fields: [
        {
          type: 'dropdown',
          name: 'type',
          values: [
            {
              name: 'at least',
              caption: 'at least'
            }
          ]
        },
        {
          type: 'percentage',
          name: 'certainty'
        },
        {
          type: 'static',
          caption: 'certain that'
        },
        {
          type: 'dropdown',
          name: 'dimension',
          values: [
            {
              name: 'Business Type',
              caption: 'Business Type'
            }
          ]
        },
        {
          type: 'static',
          caption: 'is'
        },
        {
          type: 'text',
          name: 'segment'
        }
      ]
    }
  ]

  const getThing = (item, thing) => {
    let source = null

    for (let i = 0; i < sources.length; i++) {
      const s = sources[i]

      if (s.name && s.name === item.$source) {
        source = s
        break
      }
    }

    if (source) {
      if (typeof source[thing] === 'string') {
        return source[thing]
      } else if (typeof source[thing] === 'function') {
        return source[thing](item) || ''
      }
    }

    return ''
  }

  return {
    getThing,
    sources
  }
}

export default factory
